import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { selectIsNeedToShowBlockedPaywall } from '@app/_store/features/user-plan/user-plan.selectors';
import { RefreshSnackbarComponent } from '@core/notifications/refresh-snackbar.component';
import { AuthService } from '@core/services/auth.service';
import { LocalStorageService } from '@core/services/storages/local-storage.service';
import { Store } from '@ngrx/store';
import { DateTime, Duration } from 'luxon';
import { Subscription, take, timer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppDataRefreshService {
  private store = inject(Store);
  private snackBar = inject(MatSnackBar);
  private auth = inject(AuthService);
  private localStorage = inject(LocalStorageService);
  private router = inject(Router);

  private readonly REFRESH_KEY = 'PG_last_refresh';
  private readonly INTERVAL_MINUTES = 20;
  private refreshTimeoutSub: Subscription | null = null;
  private snackRef: MatSnackBarRef<RefreshSnackbarComponent> | null = null;

  private isAppBlocked = this.store.selectSignal(
    selectIsNeedToShowBlockedPaywall
  );

  /** Last value the auth stream delivered; the prompt's final guard. */
  private isLoggedIn = false;

  init(): void {
    // The continuous stream, not the one-shot isAuthenticated(): this service
    // used to sample the session exactly once at bootstrap, so a later logout
    // never cleared the armed timer (the reminder fired over the sign-in
    // page), and a later login never armed one (the reminder silently never
    // worked for that whole session).
    this.auth.isAuthenticated$.subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn;
      if (isLoggedIn) {
        this.handleLogin();
      } else {
        this.clearTimerAndTimestamp();
      }
    });
  }

  private handleLogin(): void {
    const lastRefreshStr = this.localStorage.get(this.REFRESH_KEY);
    const now = DateTime.now();
    const refreshIntervalMs = Duration.fromObject({
      minutes: this.INTERVAL_MINUTES,
    }).as('milliseconds');

    if (!lastRefreshStr) {
      this.setReminder(refreshIntervalMs);
      return;
    }

    const lastRefreshMs = Number(lastRefreshStr);
    const lastRefresh = DateTime.fromMillis(lastRefreshMs);

    // Block 12: a stored value that is not a number leaves `diff` NaN, the
    // comparison below false, and `refreshIntervalMs - NaN` NaN — and
    // `timer(NaN)` fires straight away, so the "refresh your data" snackbar
    // appeared the moment the app loaded, on every load. An unreadable stamp
    // is treated as no stamp at all.
    if (!Number.isFinite(lastRefreshMs) || !lastRefresh.isValid) {
      this.setReminder(refreshIntervalMs);
      return;
    }

    const diff = now.diff(lastRefresh).as('milliseconds');

    if (diff >= refreshIntervalMs) {
      // This runs right after a page load or a sign-in — both just fetched the
      // data. Prompting to refresh it again in the same second covered the
      // header on every stale-stamp load (QA audit #2, B55); the load itself is
      // the refresh, so the stamp moves and the reminder is armed from now.
      this.stampNow();
      this.setReminder(refreshIntervalMs);
    } else {
      const remainingTime = refreshIntervalMs - diff;
      this.setReminder(remainingTime);
    }
  }

  private setReminder(delay: number): void {
    this.clearTimer();

    // Belt over the caller: `timer` treats a NaN or negative delay as zero,
    // which turns a reminder into an immediate prompt.
    const safeDelay =
      Number.isFinite(delay) && delay > 0
        ? delay
        : Duration.fromObject({ minutes: this.INTERVAL_MINUTES }).as(
            'milliseconds'
          );

    this.refreshTimeoutSub = timer(safeDelay)
      .pipe(take(1))
      .subscribe(() => this.showRefreshPrompt());
  }

  private showRefreshPrompt(): void {
    // Belt over the stream: whatever a stray timer believes, a signed-out
    // user is never asked to refresh data they no longer see.
    if (!this.isLoggedIn) return;

    // The paywall route, not only the store flag: on `/blocked` the plan state
    // can still be loading when the timer fires (QA audit #2, B53).
    const isAppBlocked =
      this.isAppBlocked() || this.router.url.startsWith('/blocked');

    if (isAppBlocked) {
      this.setDefaultReminder();
      return;
    }

    this.snackRef = this.snackBar.openFromComponent(RefreshSnackbarComponent, {
      duration: undefined,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['refresh-snackbar'],
    });

    this.snackRef
      .afterDismissed()
      .pipe(take(1))
      .subscribe((dismiss) => {
        if (dismiss.dismissedByAction) {
          this.dispatchRefresh();
        } else {
          this.setDefaultReminder();
        }
        this.snackRef = null;
      });
  }

  private dispatchRefresh(): void {
    this.store.dispatch(
      AppGlobalActions.loadAppData({ fields: null, reset: true })
    );

    this.stampNow();

    const refreshIntervalMs = Duration.fromObject({
      minutes: this.INTERVAL_MINUTES,
    }).as('milliseconds');
    this.setReminder(refreshIntervalMs);
  }

  private stampNow(): void {
    this.localStorage.set(
      this.REFRESH_KEY,
      DateTime.now().toMillis().toString()
    );
  }

  private setDefaultReminder(): void {
    this.setReminder(
      Duration.fromObject({ minutes: this.INTERVAL_MINUTES }).as('milliseconds')
    );
  }

  private clearTimer(): void {
    this.refreshTimeoutSub?.unsubscribe();
    this.refreshTimeoutSub = null;
  }

  private clearTimerAndTimestamp(): void {
    if (this.snackRef) {
      this.snackRef.dismiss();
      this.snackRef = null;
    }

    this.clearTimer();
    this.localStorage.delete(this.REFRESH_KEY);
  }
}
